import { useState } from "react";
import { CALL_PRICING, currencyFormatter } from "shared";

import { Modal } from "../../../common/Atoms/Modal";

type PricedCurrency = typeof CALL_PRICING.currencies[number];

/** Per minute rates from CALL_PRICING in the subscription's currency */
export function CallPricingModal({ currency, open, setOpen }: { currency: string; open: boolean; setOpen: (open: boolean) => void }) {
  const code = currency.toUpperCase() as PricedCurrency;
  const supported = CALL_PRICING.currencies.includes(code);
  const money = (cents: number) => currencyFormatter(code)(cents / 100);

  return (
    <Modal
      title={ `Call pricing` }
      open={ open }
      setOpen={ setOpen }
      width={ `lg` }
    >
      { supported ? (
        <div className={ `flex flex-col gap-5 text-sm text-gray-700` }>
          <p>
            { `Prices are per minute in ${code}, charged for each started minute once the call ends. They include call recording, transcription and the AI summary. The rate depends on the number you call from and the number you call.` }
          </p>

          <table className={ `w-full text-left` }>
            <thead className={ `border-b border-gray-200 text-xs uppercase text-gray-500` }>
              <tr>
                <th className={ `py-2 pr-4 font-medium` }>{ `Calling` }</th>
                { CALL_PRICING.callerIdGroups.map(group => (
                  <th
                    key={ group.key }
                    className={ `py-2 pr-4 font-medium` }
                  >
                    { group.label }
                  </th>
                )) }
              </tr>
            </thead>
            <tbody className={ `divide-y divide-gray-100` }>
              { CALL_PRICING.destinations.map(destination => (
                <tr key={ destination.label }>
                  <td className={ `py-2 pr-4 font-medium text-gray-900` }>{ destination.label }</td>
                  { CALL_PRICING.callerIdGroups.map(group => (
                    <td
                      key={ group.key }
                      className={ `py-2 pr-4` }
                    >
                      { money(destination.perMinuteCents[group.key][code]) }
                    </td>
                  )) }
                </tr>
              )) }
            </tbody>
          </table>

          <div>
            <p className={ `font-medium text-gray-900` }>{ `Calls that ring your phone first` }</p>
            <p className={ `mt-1` }>
              { `When we ring your own phone and then connect the call, we add the cost of the call to your phone, based on the country of the number you call from.` }
            </p>
            <table className={ `mt-2 w-full text-left` }>
              <tbody className={ `divide-y divide-gray-100` }>
                { Object.entries(CALL_PRICING.bridgeLegCents).map(([country, cents]) => (
                  <tr key={ country }>
                    <td className={ `py-2 pr-4 font-medium text-gray-900` }>{ `Calling from ${country} numbers` }</td>
                    <td className={ `py-2 pr-4` }>{ `+ ${money(cents[code])}` }</td>
                  </tr>
                )) }
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <p className={ `text-sm text-gray-700` }>{ `Calls are not priced in ${code} yet. Contact support to make calls.` }</p>
      ) }
    </Modal>
  );
}

export function CallPricingLink({ currency }: { currency: string }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type={ `button` }
        className={ `cursor-pointer font-semibold text-paidniceGreen-600 hover:text-paidniceGreen-700` }
        onClick={ () => setOpen(true) }
      >
        { `Call pricing` }
      </button>
      <CallPricingModal
        currency={ currency }
        open={ open }
        setOpen={ setOpen }
      />
    </>
  );
}
